import { Alert, Button, Card, Flex, Form, Input, Radio, Typography } from 'antd';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';

import { ApiError, NetworkError } from '@/api/client';
import { canOpen, homePath } from '@/app/navigation';
import { useAppConfig } from '@/features/config/configContext';
import { ru } from '@/i18n/ru';
import { PoweredBy } from '@/components/PoweredBy';

import { RoleRequiredError, useAuth, type Role } from './authContext';

interface LoginForm {
  login: string;
  password: string;
  role?: Role;
}

/** The mode picked last on this computer: offered first next time. */
const LAST_ROLE_KEY = 'sos.lastRole';

function lastRole(roles: readonly Role[]): Role | undefined {
  try {
    const stored = window.localStorage.getItem(LAST_ROLE_KEY);
    return roles.find((role) => role === stored);
  } catch {
    return undefined;
  }
}

function rememberRole(role: Role): void {
  try {
    window.localStorage.setItem(LAST_ROLE_KEY, role);
  } catch {
    // Only a convenience.
  }
}

function loginErrorText(error: unknown): string {
  if (error instanceof ApiError && error.status === 401) return ru.login.invalid;
  if (error instanceof ApiError && error.status === 403) return ru.login.modeNotGranted;
  if (error instanceof ApiError && error.status === 429) {
    const seconds = Number(error.headers.get('Retry-After'));
    return ru.login.tooMany(Number.isFinite(seconds) && seconds > 0 ? seconds : 60);
  }
  if (error instanceof NetworkError) return ru.login.network;
  return ru.login.failed;
}

/** Return target after login: only a local path the role may open (no open redirect). */
function targetFor(role: Role, from: unknown): string {
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) {
    if (canOpen(role, from)) return from;
  }
  return homePath(role);
}

export function LoginPage() {
  const { state, login } = useAuth();
  const config = useAppConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm<LoginForm>();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Known once the password was accepted for an account with several roles.
  const [roles, setRoles] = useState<Role[] | null>(null);
  const from = (location.state as { from?: unknown } | null)?.from;

  if (state.status === 'authenticated') {
    return <Navigate to={targetFor(state.user.role, from)} replace />;
  }

  async function onFinish(values: LoginForm) {
    setSubmitting(true);
    setError(null);
    try {
      const user = await login(values.login.trim(), values.password, values.role);
      if (values.role) rememberRole(values.role);
      void navigate(targetFor(user.role, from), { replace: true });
    } catch (caught) {
      if (caught instanceof RoleRequiredError) {
        setRoles(caught.roles);
        const preferred = lastRole(caught.roles);
        if (preferred) form.setFieldValue('role', preferred);
      } else {
        setError(loginErrorText(caught));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Flex vertical align="center" justify="center" gap={12} style={{ minHeight: '100vh', padding: 16 }}>
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <Typography.Title level={3} style={{ marginTop: 0 }}>
          {config.org_name || config.product_name}
        </Typography.Title>
        <Typography.Paragraph type="secondary">{ru.login.title}</Typography.Paragraph>
        <Form<LoginForm>
          form={form}
          layout="vertical"
          requiredMark={false}
          disabled={submitting}
          onFinish={(values) => void onFinish(values)}
        >
          <Form.Item
            name="login"
            label={ru.login.login}
            rules={[{ required: true, whitespace: true, message: ru.login.loginRequired }]}
          >
            <Input
              autoFocus
              autoComplete="username"
              size="large"
              onChange={() => {
                // Another account may have other roles.
                setRoles(null);
                form.setFieldValue('role', undefined);
              }}
            />
          </Form.Item>
          <Form.Item
            name="password"
            label={ru.login.password}
            rules={[{ required: true, message: ru.login.passwordRequired }]}
          >
            <Input.Password autoComplete="current-password" size="large" />
          </Form.Item>
          {roles && (
            <Form.Item
              name="role"
              label={ru.login.mode}
              extra={ru.login.chooseMode}
              rules={[{ required: true, message: ru.login.modeRequired }]}
            >
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                size="large"
                options={roles.map((role) => ({ value: role, label: ru.roles[role] }))}
              />
            </Form.Item>
          )}
          {error && (
            <Form.Item>
              <Alert type="error" showIcon message={error} role="alert" />
            </Form.Item>
          )}
          <Button type="primary" htmlType="submit" size="large" block loading={submitting}>
            {ru.login.submit}
          </Button>
        </Form>
      </Card>
      {/* What the system runs on (STAGE_7 7.7), under the sign-in. */}
      <PoweredBy />
    </Flex>
  );
}
